/** ***********************************************************************
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ************************************************************************* */

/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import Button from '@react/react-spectrum/Button';
import DropdownButton from '@react/react-spectrum/DropdownButton';
import { MenuItem } from '@react/react-spectrum/Menu';
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger';
import Tooltip from '@react/react-spectrum/Tooltip';
import Wait from '@react/react-spectrum/Wait';
import uuid from 'uuid';
import Icons from '../../../../../utils/iconMapping';
import TranslationUtil from '../../../../../utils/translation';
import { appTileAnalytics } from '../../../../../analytics';
import { ACTION_ID } from '../../../../../utils/modelMapper/appModelMapper/appModelConstants';
import './index.scss';
import { interpretKey } from '../../../../../utils/eventUtil';
import { ANALYTICS } from '../../../../../utils/enums';
import { AppAction } from '../../../../../stores/app';
import EllipsisAnimation from '../../../../../components/ellipsisAnimation';

const hasCTAOrDropdown = (appSectionBasedFields) => {
    const { dropDownList = [], buttonList = [] } = appSectionBasedFields;
    return ((buttonList ? buttonList.length : 0) + (dropDownList ? dropDownList.length : 0)) > 0;
};

const getMessage = TranslationUtil.getMessage.bind(TranslationUtil);

const renderDropdown = (
    app, appSectionBasedFields, sectionHeader, appSectionFilter, layoutId,
) => {
    if (!appSectionBasedFields.showDropDown) {
        return null;
    }

    const {
        onAppAction,
    } = app;
    const {
        dropDownList = [],
    } = appSectionBasedFields;
    if (!dropDownList || !dropDownList
        .filter(dropDown => !dropDown.hidden).length) {
        return (<span className="empty-list-view-div" />);
    }
    return (
        <span className="listView-appTile-footer-dropDown">
            <OverlayTrigger trigger={['hover', 'focus']} placement="top" delayShow={1000}>
                <DropdownButton
                    alignRight
                    className="listView-appTile-footer-dropDownButton"
                    onClick={(e) => {
                        if (e) {
                            e.stopPropagation();
                        }
                    }}
                    aria-label="Options"
                    role="button"
                    onKeyDown={interpretKey({
                        onEnter: [null, true],
                    })}>
                    {dropDownList
                        .filter(dropDown => !dropDown.hidden)
                        .map((dropDown) => {
                            const {
                                localizedText, actionId, actionType = {},
                            } = dropDown;
                            const menuItem = (
                                <MenuItem
                                    key={localizedText}
                                    className={classnames('listView-appTile-footer-dropDownMenuItem', { errorText: actionType.textType === 'error' })}
                                    onClick={() => {
                                        const guid = uuid.v4();
                                        appTileAnalytics(ANALYTICS.EVENTS.APP_MENU_CLICK, {
                                            actionId,
                                            app,
                                            appSectionFilter,
                                            sectionHeader,
                                            guid,
                                            viewType: ANALYTICS.VIEW_TYPE[layoutId],
                                        });
                                        onAppAction(actionId, app, {
                                            guid,
                                            source: ANALYTICS.APPS,
                                            inAppSource: ANALYTICS.APP_CARD,
                                        });
                                    }}
                                    disabled={actionType.isDisabled}
                                    aria-label={localizedText}
                                    value={localizedText}>
                                    {localizedText}
                                </MenuItem>
                            );
                            return menuItem;
                        })}
                </DropdownButton>
                <Tooltip className="listView-appTile-footer-dropDownButton-tooltip">
                    {TranslationUtil.getMessage('ccx.common.moreactions')}
                </Tooltip>
            </OverlayTrigger>
        </span>
    );
};

const renderCTAs = (app, appSectionBasedFields, sectionHeader, appSectionFilter, layoutId) => {
    const { Alert } = Icons;
    const { onAppAction, isPDPDataAvailable } = app;
    let { buttonList = [] } = appSectionBasedFields;
    const { displayLearnMoreIcon } = appSectionBasedFields;

    // Render learn more as CTA if no other CTA or dropdown
    if (isPDPDataAvailable && displayLearnMoreIcon && !hasCTAOrDropdown(appSectionBasedFields)) {
        const learnMoreLabel = TranslationUtil.getMessage('ccx.common.learnMore');
        buttonList = [
            new AppAction({
                actionId: ACTION_ID.LEARN_MORE,
                actionType: {
                    variant: 'primary',
                },
                localizedText: learnMoreLabel,
                ariaLabel: learnMoreLabel,
            }),
        ];
    }

    return (
        buttonList
            .filter(button => !button.hidden)
            .map((button) => {
                const {
                    actionType = {}, iconType,
                    localizedText, actionId, title,
                } = button;
                const isQuiet = (actionType.quiet === true);
                const errorIcon = (iconType ? <Alert variant={iconType} size="S" /> : null);
                const actionButton = (
                    <Button
                        className={`listView-appTile-footer-button ${errorIcon ? 'appAction-warning' : ''} ${actionId === ACTION_ID.LEARN_MORE ? 'handle-learn-more' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            const guid = uuid.v4();
                            appTileAnalytics(ANALYTICS.EVENTS.APP_BUTTON_CLICK, {
                                actionId,
                                app,
                                appSectionFilter,
                                sectionHeader,
                                guid,
                                viewType: ANALYTICS.VIEW_TYPE[layoutId],
                            });
                            onAppAction(actionId, app, {
                                guid,
                                source: ANALYTICS.APPS,
                                inAppSource: ANALYTICS.APP_CARD,
                            });
                        }}
                        onKeyDown={interpretKey({
                            onEnter: [null, true],
                        })}
                        quiet={isQuiet}
                        variant={actionType.variant}
                        disabled={actionType.isDisabled}
                        title={title}
                        label={localizedText}
                        aria-label={localizedText}
                        role="button"
                        icon={errorIcon}
                        key={localizedText} />
                );
                return actionButton;
            }));
};

const renderCTAsandLinks = ({
    app, appSectionBasedFields, sectionHeader, appSectionFilter, layoutId,
}) => (
    <div className="spectrum-Card-footer">
        {renderCTAs(app, appSectionBasedFields, sectionHeader, appSectionFilter, layoutId)}
        {renderDropdown(app, appSectionBasedFields, sectionHeader, appSectionFilter, layoutId)}
    </div>
);

const renderProgress = ({
    app, sectionHeader, appSectionFilter, layoutId, appSectionBasedFields,
}) => {
    const {
        progress = null,
        onAppAction,
    } = app;
    const { localizedMessageText } = progress;
    let progressContent;
    const cancelAction = (e) => {
        e.stopPropagation();
        onAppAction(ACTION_ID.PROGRESS_CANCEL, app);
        appTileAnalytics(ANALYTICS.EVENTS.APP_PROGRESS_CANCEL, {
            actionId: ACTION_ID.PROGRESS_CANCEL,
            app,
            appSectionFilter,
            sectionHeader,
            progress: progress.percentage * 100,
            viewType: ANALYTICS.VIEW_TYPE[layoutId],
        });
    };
    if (progress.isWaiting) {
        progressContent = (
            <div className="listView-appTile-footer-isWaiting-container">
                <div className="listView-appTile-footer-isWaiting-progress">
                    {localizedMessageText ? `${localizedMessageText}...` : ''}
                </div>
            </div>
        );
    } else {
        progressContent = (
            <div className="listView-appTile-footer-progress-container">
                <div className="listView-appTile-footer-progress">
                    <Wait className="icon" indeterminate={false} value={progress.percentage * 100} size="S" />
                    <div className="listView-appTile-footer-progress-bar-text">
                        <div className="listView-appTile-footer-progress-bar-left-text">
                            {localizedMessageText ? `${localizedMessageText}` : ''}
                        </div>
                        <EllipsisAnimation />
                        <div className="listView-appTile-footer-progress-bar-right-text">
                            ({TranslationUtil.getMessage(
                                'ccx.progress.percentage',
                                { percentage: Math.round(progress.percentage * 100) }
                            )})
                        </div>
                    </div>

                </div>
            </div>
        );
    }
    return (
        <div className="listView-appTile-progress-container">
            <div className="listView-appTile-progress-content">
                { progressContent }
            </div>
            <div className={(classnames('listView-appTile-progress-cancel', {
                'progress-cancel-no-dropDown': (!appSectionBasedFields.showDropDown),
            }))}>
                {
                    progress.isCancellable && (
                        <Button
                            className="progress-cancel-button"
                            variant="primary"
                            quiet
                            aria-label={getMessage('ccx.confirmation.btn.cancel')}
                            onClick={cancelAction}
                            onKeyDown={interpretKey({
                                onEnter: [cancelAction, true],
                            })}
                            label={TranslationUtil.getMessage('ccx.confirmation.btn.cancel')} />
                    )
                }
            </div>
        </div>
    );
};

export default @observer class AppTileFooter extends Component {
    render() {
        const {
            app,
            appSectionBasedFields,
            sectionHeader,
            appSectionFilter,
            renderLabel,
            showLabel,
            layoutId,
        } = this.props;

        const {
            subText, statusLabelList, isCategorySection, localizedUpdateDescription = [],
        } = appSectionBasedFields;

        const { progress } = app;
        let content;
        if (progress) {
            content = renderProgress({
                app,
                sectionHeader,
                appSectionFilter,
                layoutId,
                appSectionBasedFields,
            });
        } else {
            content = [
                showLabel && renderLabel({
                    app, appSectionFilter, subText, statusLabelList,
                }),
                renderCTAsandLinks({
                    app, appSectionBasedFields, sectionHeader, appSectionFilter, layoutId,
                }),
            ];
        }

        return (
            <div
                className={(classnames('listView-appTile-footer-container', {
                    'no-update-desc': (showLabel || !localizedUpdateDescription.length) && !isCategorySection,
                }))}>
                {content}
            </div>
        );
    }
}
